<template lang="pug">
    v-form.c-form(autocomplete="off")
        v-container(fluid grid-list-lg)
            v-layout(raw wrap)
                v-flex(xs12 sm6)

                    // Form title (Meditation Details)
                    title-component(
                        type="h4"
                        :title="$t('sessions.general.subtitleSessionDetails')"
                    )

                    // Meditation Title input field
                    v-text-field(
                        name="title"
                        outline
                        v-model.lazy="model.title"
                        v-validate="'min:2'"
                        :data-vv-as="$t('fields.sessionName')"
                        :error-messages="errors.collect('title')"
                        :label="$t('fields.sessionName')"
                    )

                    // Meditation Title input field
                    v-text-field(
                        name="feedLabel"
                        outline
                        v-model.lazy="model.feedLabel"
                        v-validate="'min:2'"
                        :data-vv-as="$t('fields.feedLabel')"
                        :error-messages="errors.collect('feedLabel')"
                        :label="$t('fields.feedLabel')"
                    )

                    // Meditation Title input field
                    v-text-field(
                        name="author"
                        outline
                        required
                        v-model.lazy="model.description"
                        v-validate="'min:2'"
                        :data-vv-as="$t('fields.author')"
                        :error-messages="errors.collect('description')"
                        :label="$t('fields.author')"
                    )

                v-flex(xs12 sm6)

                    title-component(
                        type="h4"
                        :title="$t('sessions.general.subtitleUploadPhoto')"
                    )

                    div.uploadWrapper.mt-3

                        div
                            // Upload image button
                            upload-button.ml-0(
                                id="avatar"
                                :isLoading="isLoading"
                                :disabled="isLoading"
                                :ext="'image/jpeg,image/png,image/jpg'"
                                :selectedCallback="uploadSessionImage"
                            )
                                span {{ $t('defaultButtons.uploadImage') }}

                    div.mb-2.fileName.ellipsis {{ model.poster_name }}


                    // Assign to all businesses checkbox
                    v-checkbox.mt-0(
                        v-model="model.sendPush"
                        :label="$t('fields.sendPush')"
                    )

            v-layout(raw wrap)
                v-flex(xs12)

                    // Horizontal line
                    separator-component

                    div.actions
                        div

                            // Delete button
                            a.deleteAction(
                                v-if="isEditing"
                                :isLoading="isLoading"
                                @click="deleteSession"
                            )
                                v-icon(grey) delete
                                | {{ $t('sessions.edit.delete.button') }}

                        div.controls
                            div
                                // Cancel button
                                button-component.ml-0(
                                    color="grey"
                                    large
                                    outline
                                    :isLoading="isLoading"
                                    :action="cancel"
                                ) {{ $t('defaultButtons.cancel') }}

                            div
                                // Submit button
                                button-component.mr-0.submit(
                                    color="primary"
                                    large
                                    :isLoading="isLoading"
                                    :action="submit"
                                ) {{ $t('defaultButtons.save') }}
</template>

<script>
    import Button from '~components/Button';
    import Separator from '~components/Separator';
    import Title from '~components/Title';
    import VideoUpload from '~components/VideoUpload';
    import UploadButton from '~components/UploadButton';
    import { mapFields } from 'vuex-map-fields';
    import { mapActions, mapGetters } from 'vuex';
    import dayjs from 'dayjs';
    import VueTypes from 'vue-types';

    export default {
        $_veeValidate: {
            validator: 'new'
        },
        components: {
            VideoUpload,
            UploadButton,
            ButtonComponent: Button,
            SeparatorComponent: Separator,
            TitleComponent: Title
        },
        props: {
            isEditing: VueTypes.bool.def(false),
            id: VueTypes.string.def('')
        },
        data() {
            return {
                file: null,
                currentDay: dayjs().format('YYYY-MM-DD')
            };
        },
        computed: {
            ...mapFields('mindfulMinute', {
                model: 'session'
            }),
            ...mapGetters('mindfulMinute', ['isLoading'])
        },
        created() {
            this.resetSession();
            if (this.isEditing) {
                this.read(this.id);
            }
        },
        methods: {
            ...mapActions('mindfulMinute', [
                'create',
                'resetSession',
                'read',
                'update',
                'cancel',
                'deleteSession'
            ]),
            ...mapActions('sessions', ['uploadImage']),
            ...mapActions('snackbar', ['showMessage']),

            /**
             * Update Session on submit
             * @returns {Promise<void>}
             */
            async submit() {
                // Validate all fields and check in no errors
                const validation = await this.$validator.validateAll();
                if (!validation) return;

                if (!this.model.poster) {
                    return this.showMessage(
                        this.$t('sessions.edit.errorMessageAvatar')
                    );
                }

                if (this.isEditing) return this.update();

                return this.create();
            },

            /**
             * Upload meditation image
             * @param {file} e
             * @returns {Promise<void>}
             */
            async uploadSessionImage(e) {
                let formData = new FormData();
                formData.append('name', e.name);
                formData.append('photo', e);
                const resp = await this.uploadImage(formData);

                if (resp.ok) {
                    this.model.poster = resp.data;
                    this.model.poster_name = e.name;
                }
            },

            /**
             * Upload video callback
             * @param link
             */
            uploadVideoCallback(params) {
                this.model.video = params.url;
                this.model.videoName = params.videoName;
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'

    >>> .v-textarea textarea
        resize: none

    .marginRight
        margin-right: 20px

    .marginTop
        margin-top: 20px

    .fileName
        max-width: 50%
        margin-bottom: 15px

    .saveSettings
        display: flex
        align-items flex-end

        .expireOn
            flex: 1 1 50%

        .replaceWith
            flex: 1 1 50%

        .v-input--selection-controls
            margin-top: 0

    .actions
        display: flex
        align-items: center
        justify-content: space-between

        .deleteAction
            display: flex
            align-items: center
            text-decoration: none
            font-size: 14px
            color: $theme.white

            .v-icon
                padding-right: 5px
                color: $theme.darkGrey

        .controls
            width: auto

    .coverPhotoWrapper
        margin-top: 12px

    .uploadWrapper
        display: flex
        align-items: center
        flex-direction: row

    .removeButton
        padding-left: 5px

    .ellipsis
        text-overflow: ellipsis
        white-space: nowrap
        overflow: hidden
        max-width: 100%

    >>> .v-select__selection
        display: inline-block
        overflow: hidden
        white-space: nowrap
        text-overflow: ellipsis

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .saveSettings
            display: flex
            align-items flex-start
            flex-direction: column

            .expireOn
                flex: 1 1 100%
                width: 100%

            .replaceWith
                flex: 1 1 100%
                width: 100%

        .marginRight
            margin-right: 0

        .coverPhotoWrapper
            margin-top: 9px

    .uploadWrapper
        display: flex
        align-items: center

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .actions
            flex-direction: column
            justify-content: space-between

            .deleteAction
                margin-bottom: 10px

            .controls
                width: 100%

                div
                    width: 50%
</style>
